import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {createSubject, getParentSubjects} from "../../../services/subjectsApi";
import Select from "../select/Select";
import styles from "./TopicSelector.module.css";

const NEW_FENN_VALUE = "__new__";

const TopicSelector = ({
                           value,
                           onChange,
                           topics,
                           onTopicCreated,
                           placeholder,
                           disabled = false,
                           // Optional "add-style" extras (used by the exam wizard). When `onAddAll` is set, each
                           // subject group shows an "Add all" button that bulk-adds every topic of that subject.
                           // `selectedIds` lets the panel mark already-added topics so the user sees what's in.
                           onAddAll,
                           selectedIds,
                       }) => {
    const {t} = useTranslation();
    const wrapRef = useRef(null);
    const searchRef = useRef(null);
    const topicNameRef = useRef(null);
    const newFennNameRef = useRef(null);

    const isEmpty = !topics || topics.length === 0;
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("list"); // "list" | "create"
    const [search, setSearch] = useState("");

    // create form state
    const [parents, setParents] = useState([]);
    const [parentsLoaded, setParentsLoaded] = useState(false);
    const [selectedFennId, setSelectedFennId] = useState("");
    const [newFennName, setNewFennName] = useState("");
    const [creatingNewFenn, setCreatingNewFenn] = useState(false);
    const [topicName, setTopicName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState("");

    const selectedTopic = useMemo(
        () => (topics || []).find((tp) => Number(tp.id) === Number(value)) || null,
        [topics, value]
    );

    // Topics already added in the parent (add-style usage), so the panel can mark them and disable a
    // group's "Add all" once everything in it is in.
    const selectedSet = useMemo(
        () => new Set((selectedIds || []).map(Number)),
        [selectedIds]
    );

    const filteredTopics = useMemo(() => {
        const list = topics || [];
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter((tp) => {
            const inTopic = (tp.title || "").toLowerCase().includes(q);
            const inParent = (tp.parentName || "").toLowerCase().includes(q);
            return inTopic || inParent;
        });
    }, [topics, search]);

    const grouped = useMemo(() => {
        const map = new Map();
        filteredTopics.forEach((tp) => {
            const key = tp.parentId ? String(tp.parentId) : "_orphan";
            if (!map.has(key)) {
                map.set(key, {parentId: tp.parentId, parentName: tp.parentName || "—", items: []});
            }
            map.get(key).items.push(tp);
        });
        return Array.from(map.values());
    }, [filteredTopics]);

    // Outside click + Escape close
    useEffect(() => {
        if (!open) return;
        const close = () => {
            setOpen(false);
            setMode("list");
            setSearch("");
            setSelectedFennId("");
            setNewFennName("");
            setCreatingNewFenn(false);
            setTopicName("");
            setFormError("");
            setSubmitting(false);
        };
        const onDocClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                close();
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") close();
        };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    // Auto-focus search / first input when panel opens
    useEffect(() => {
        if (!open) return;
        if (mode === "list") {
            searchRef.current?.focus();
        } else if (mode === "create") {
            // wait a tick for render
            setTimeout(() => {
                if (creatingNewFenn) newFennNameRef.current?.focus();
                else topicNameRef.current?.focus();
            }, 0);
        }
    }, [open, mode, creatingNewFenn]);

    // If list is empty when opened, jump straight to create
    useEffect(() => {
        if (open && isEmpty && mode === "list") {
            enterCreateMode();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isEmpty]);

    const openPanel = () => {
        if (disabled) return;
        setOpen(true);
        setSearch("");
    };

    const closePanel = () => {
        setOpen(false);
        setMode("list");
        setSearch("");
        resetCreateForm();
    };

    const resetCreateForm = () => {
        setSelectedFennId("");
        setNewFennName("");
        setCreatingNewFenn(false);
        setTopicName("");
        setFormError("");
        setSubmitting(false);
    };

    const ensureParentsLoaded = async () => {
        if (parentsLoaded) return;
        try {
            const data = await getParentSubjects();
            setParents(Array.isArray(data) ? data : []);
            setParentsLoaded(true);
        } catch (e) {
            // surface as form error so user is not stuck silently
            setFormError(
                e?.response?.data?.message ||
                e?.message ||
                t("pages.topicSelector.loadFennsError")
            );
        }
    };

    const enterCreateMode = () => {
        setMode("create");
        ensureParentsLoaded();
    };

    const exitCreateMode = () => {
        setMode("list");
        resetCreateForm();
    };

    const handleSelect = (id) => {
        const tp = (topics || []).find((t) => Number(t.id) === Number(id)) || null;
        onChange?.(id, tp);
        closePanel();
    };

    const handleFennChange = (e) => {
        const v = e.target.value;
        setFormError("");
        if (v === NEW_FENN_VALUE) {
            setCreatingNewFenn(true);
            setSelectedFennId("");
        } else {
            setCreatingNewFenn(false);
            setSelectedFennId(v);
        }
    };

    const handleSubmitCreate = async (e) => {
        e?.preventDefault?.();
        setFormError("");

        let parentId = selectedFennId ? Number(selectedFennId) : null;

        // Inline-create the Fənn first if needed
        if (creatingNewFenn) {
            const trimmedFenn = newFennName.trim();
            if (!trimmedFenn) {
                setFormError(t("pages.topicSelector.fennNameRequired"));
                return;
            }
            try {
                setSubmitting(true);
                const res = await createSubject({name: trimmedFenn});
                const newId = res?.data?.id;
                if (!newId) throw new Error(t("pages.topicSelector.createFailed"));
                parentId = newId;
                setParents((prev) => [...prev, {id: newId, name: trimmedFenn}]);
                setSelectedFennId(String(newId));
                setCreatingNewFenn(false);
                setNewFennName("");
                toast.success(t("pages.topicSelector.fennCreatedToast"));
            } catch (err) {
                setSubmitting(false);
                setFormError(
                    err?.response?.data?.message ||
                    err?.message ||
                    t("pages.topicSelector.createFailed")
                );
                return;
            }
        }

        if (!parentId) {
            setSubmitting(false);
            setFormError(t("pages.topicSelector.fennRequired"));
            return;
        }

        const trimmedTopic = topicName.trim();
        if (!trimmedTopic) {
            setSubmitting(false);
            setFormError(t("pages.topicSelector.topicNameRequired"));
            return;
        }

        try {
            setSubmitting(true);
            const res = await createSubject({name: trimmedTopic, parentId});
            const newTopicId = res?.data?.id;
            if (!newTopicId) throw new Error(t("pages.topicSelector.createFailed"));

            const fenn = parents.find((p) => Number(p.id) === Number(parentId));
            const newTopic = {
                id: newTopicId,
                title: trimmedTopic,
                questionCount: 0,
                parentId,
                parentName: fenn?.name || "",
            };

            toast.success(t("pages.topicSelector.topicCreatedToast"));
            onTopicCreated?.(newTopic);
            onChange?.(newTopicId, newTopic);
            closePanel();
        } catch (err) {
            setSubmitting(false);
            setFormError(
                err?.response?.data?.message ||
                err?.message ||
                t("pages.topicSelector.createFailed")
            );
        }
    };

    const triggerLabel = selectedTopic
        ? selectedTopic.parentName
            ? `${selectedTopic.parentName} › ${selectedTopic.title}`
            : selectedTopic.title
        : placeholder || t("pages.topicSelector.selectTopic");

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <button
                type="button"
                className={`${styles.trigger} ${selectedTopic ? "" : styles.triggerEmpty}`}
                onClick={() => (open ? closePanel() : openPanel())}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={styles.triggerLabel}>{triggerLabel}</span>
                <span className={styles.chev} aria-hidden>▾</span>
            </button>

            {open && (
                <div className={styles.panel} role="dialog">
                    {mode === "list" && (
                        <>
                            <div className={styles.searchRow}>
                                <input
                                    ref={searchRef}
                                    type="text"
                                    className={styles.search}
                                    placeholder={t("pages.topicSelector.searchPlaceholder")}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>

                            <div className={styles.list} role="listbox">
                                {grouped.length === 0 && (
                                    <div className={styles.empty}>
                                        {t("pages.topicSelector.noResults")}
                                    </div>
                                )}
                                {grouped.map((g) => {
                                    const allAdded = onAddAll && g.items.every((tp) => selectedSet.has(Number(tp.id)));
                                    return (
                                    <div key={g.parentId || "orphan"} className={styles.group}>
                                        <div className={styles.groupHeader}>
                                            <span className={styles.groupHeaderName}>{g.parentName}</span>
                                            {onAddAll && g.items.length > 1 && (
                                                <button
                                                    type="button"
                                                    className={styles.groupAddAll}
                                                    onClick={() => onAddAll(g.items)}
                                                    disabled={allAdded}
                                                >
                                                    {allAdded
                                                        ? t("pages.topicSelector.allAdded")
                                                        : t("pages.topicSelector.addAll")}
                                                </button>
                                            )}
                                        </div>
                                        {g.items.map((tp) => {
                                            const isAdded = selectedSet.has(Number(tp.id));
                                            const isSelected = Number(tp.id) === Number(value) || isAdded;
                                            return (
                                                <button
                                                    key={tp.id}
                                                    type="button"
                                                    role="option"
                                                    aria-selected={isSelected}
                                                    className={`${styles.option} ${isSelected ? styles.optionSelected : ""}`}
                                                    onClick={() => handleSelect(tp.id)}
                                                >
                                                    <span className={styles.optionTitle}>{tp.title}</span>
                                                    <span className={styles.optionRight}>
                                                        {isAdded && (
                                                            <span className={styles.optionCheck} aria-hidden>✓</span>
                                                        )}
                                                        {typeof tp.questionCount === "number" && (
                                                            <span className={styles.optionMeta}>
                                                                {tp.questionCount}
                                                            </span>
                                                        )}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    );
                                })}
                            </div>

                            <button
                                type="button"
                                className={styles.footerBtn}
                                onClick={enterCreateMode}
                            >
                                <span className={styles.plus} aria-hidden>+</span>
                                {t("pages.topicSelector.createNewTopic")}
                            </button>
                        </>
                    )}

                    {mode === "create" && (
                        <div className={styles.createForm}>
                            <div className={styles.createHeader}>
                                <button
                                    type="button"
                                    className={styles.backBtn}
                                    onClick={exitCreateMode}
                                    disabled={submitting}
                                    aria-label={t("pages.topicSelector.back")}
                                >
                                    ←
                                </button>
                                <span className={styles.createTitle}>
                                    {t("pages.topicSelector.createNewTopicTitle")}
                                </span>
                            </div>

                            {isEmpty && (
                                <div className={styles.helper}>
                                    {t("pages.topicSelector.firstTopicHelper")}
                                </div>
                            )}

                            {formError && (
                                <div className={styles.formError} role="alert">
                                    {formError}
                                </div>
                            )}

                            <div className={styles.field}>
                                <label className={styles.label}>
                                    {t("pages.topicSelector.fennLabel")}
                                </label>
                                {!creatingNewFenn ? (
                                    <Select
                                        className={styles.input}
                                        value={selectedFennId}
                                        onChange={handleFennChange}
                                        disabled={submitting}
                                        options={[
                                            { value: "", label: t("pages.topicSelector.fennPlaceholder") },
                                            ...parents.map((p) => ({ value: p.id, label: p.name })),
                                            { value: NEW_FENN_VALUE, label: `+ ${t("pages.topicSelector.createNewFenn")}` },
                                        ]}
                                    />
                                ) : (
                                    <div className={styles.inlineRow}>
                                        <input
                                            ref={newFennNameRef}
                                            type="text"
                                            className={styles.input}
                                            placeholder={t("pages.topicSelector.fennNamePlaceholder")}
                                            value={newFennName}
                                            onChange={(e) => setNewFennName(e.target.value)}
                                            disabled={submitting}
                                            maxLength={100}
                                        />
                                        <button
                                            type="button"
                                            className={styles.linkBtn}
                                            onClick={() => {
                                                setCreatingNewFenn(false);
                                                setNewFennName("");
                                                setFormError("");
                                            }}
                                            disabled={submitting}
                                        >
                                            {t("pages.topicSelector.useExistingFenn")}
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className={styles.field}>
                                <label className={styles.label}>
                                    {t("pages.topicSelector.topicLabel")}
                                </label>
                                <input
                                    ref={topicNameRef}
                                    type="text"
                                    className={styles.input}
                                    placeholder={t("pages.topicSelector.topicNamePlaceholder")}
                                    value={topicName}
                                    onChange={(e) => setTopicName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleSubmitCreate(e);
                                    }}
                                    disabled={submitting}
                                    maxLength={100}
                                />
                            </div>

                            <div className={styles.formActions}>
                                <button
                                    type="button"
                                    className={styles.cancelBtn}
                                    onClick={exitCreateMode}
                                    disabled={submitting}
                                >
                                    {t("pages.topicSelector.cancel")}
                                </button>
                                <button
                                    type="button"
                                    className={styles.submitBtn}
                                    onClick={handleSubmitCreate}
                                    disabled={submitting}
                                >
                                    {submitting
                                        ? t("pages.topicSelector.creating")
                                        : t("pages.topicSelector.create")}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TopicSelector;
